import { useMutation } from '@apollo/react-hooks';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled, { ThemeProvider } from 'styled-components';
import darkTheme from 'themes/darkTheme';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import storage from 'shared/storage';
import { PROMOTER_SIGNUP_V2 } from '../../gql/promoter';
import ProgressHeader from '../components/ProgressHeader';
import { useSignup } from '../context/SignupContext';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  padding: 32px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 1200px) {
    max-width: 800px;
  }
`;

const CardSubtitle = styled.p`
  font-size: 15px;
  color: #94a3b8;
  margin-bottom: 32px;
  line-height: 1.6;
`;

const IntroCard = styled(Card)`
  margin-bottom: 24px;
`;

const SectionCard = styled(Card)`
  margin-bottom: 24px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const SectionContainer = styled.div``;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    width: 20px;
    height: 20px;
    color: #fa4616;
  }
`;

const EditButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #94a3b8;
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #fa4616;
    color: #fa4616;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.span`
  font-size: 12px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 15px;
  color: #ffffff;
`;

const SectionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
  align-items: stretch;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }

  /* Ensure cards inside grid stretch to equal height */
  ${SectionCard} {
    height: 100%;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 8px;

  button {
    width: auto;
    min-width: 140px;
    flex: 0 0 auto;
  }

  @media (max-width: 768px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

const ReviewStep = () => {
  const { formData, getTotalSteps, getStepNumber } = useSignup();
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [promoterSignupV2] = useMutation(PROMOTER_SIGNUP_V2);

  // Helper to safely display form data (handles objects/events)
  const safeDisplay = (value) => {
    if (!value) return 'Not provided';
    if (typeof value === 'object' && !Array.isArray(value))
      return 'Not provided';
    return value;
  };

  // Check if user info section has any data
  const hasUserInfo =
    formData.first_name ||
    formData.last_name ||
    formData.email ||
    formData.zipcode;

  const handleEdit = (step) => {
    history.push(`/promoter-signup/${step}`);
  };

  const handleBack = () => {
    history.push('/promoter-signup/terms');
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Helper to clean address objects (remove UI-only fields)
      const cleanAddress = (address) => {
        if (!address) return address;
        const cleaned = { ...address };
        delete cleaned.same_as_track;
        delete cleaned.same_as_shipping;
        return cleaned;
      };

      // Auto-populate primary_contact and title from user info
      const primaryContact = `${formData.first_name || ''} ${
        formData.last_name || ''
      }`.trim();

      // Prepare backend mutation payload
      const payload = {
        // Phone Information (from login screen)
        phone: formData.phone,
        calling_code: formData.calling_code,
        country_code: formData.country_code,

        // User Information (if new user)
        ...(formData.first_name && {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          zipcode: formData.zipcode,
        }),

        // Business Information
        promoter_type: formData.promoter_type,
        business_type: formData.business_type,
        business_dba: formData.business_dba,
        legal_name: formData.legal_name,
        business_phone: formData.business_phone,
        business_country_code: formData.business_country_code,
        currency: 'USD',
        business_email: formData.business_email,

        // Auto-populated contact fields
        primary_contact: primaryContact,
        promoter_name: primaryContact,
        title: formData.user_role || undefined,
        promoter_email: formData.business_email,
        promoter_phone: formData.business_phone,

        // Account Owner & Payouts Contact
        is_primary_owner: formData.is_primary_owner,
        ...(formData.is_primary_owner === false &&
          formData.account_owner && {
            account_owner: {
              first_name: formData.account_owner.first_name,
              last_name: formData.account_owner.last_name,
              email: formData.account_owner.email,
              cellphone: formData.account_owner.cellphone,
              calling_code: formData.account_owner.calling_code || '1',
              country_code: formData.account_owner.country_code || 'US',
            },
          }),
        has_separate_payouts_contact: formData.has_separate_payouts_contact,
        ...(formData.has_separate_payouts_contact &&
          formData.payouts_contact && {
            payouts_contact: {
              first_name: formData.payouts_contact.first_name,
              last_name: formData.payouts_contact.last_name,
              email: formData.payouts_contact.email,
              cellphone: formData.payouts_contact.cellphone,
              calling_code: formData.payouts_contact.calling_code || '1',
              country_code: formData.payouts_contact.country_code || 'US',
            },
          }),
        user_role: formData.user_role || undefined,

        // Addresses (cleaned to remove UI-only fields)
        ...(formData.promoter_type === 'track' && {
          track_address: cleanAddress(formData.track_address),
        }),
        shipping_address: cleanAddress(formData.shipping_address),
        mailing_address: cleanAddress(formData.mailing_address),
      };

      // Call the promoterSignupV2 mutation
      const result = await promoterSignupV2({
        variables: { input: payload },
      });

      // Store JWT token for authentication
      if (result.data?.promoterSignupV2?.jwt) {
        storage.set('jwt', result.data.promoterSignupV2.jwt);
      }

      // Set the new account as active
      const newAccountId = result.data?.promoterSignupV2?.account?.id;

      if (newAccountId) {
        storage.set('accountId', newAccountId);
      }

      // Store user object for auth system (required for route guards)
      // Note: jwt must be inside user object for Apollo client to find it
      storage.set('user', {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        cellphone: `+${formData.calling_code}${formData.phone}`,
        role: 'track',
        jwt: result.data?.promoterSignupV2?.jwt,
      });

      // Clean up signup-related storage items
      storage.removeItem('track-address');
      storage.removeItem('shipping-address');
      storage.removeItem('promoter-user');
      storage.removeItem('promoter-user-phone');
      storage.removeItem('promoter-signup-data');
      storage.removeItem('showStripe');
      storage.removeItem('step');

      toast.success('Account created successfully!');

      // Redirect to track admin home page
      history.push('/admin-track/home');
    } catch (error) {
      console.error('Promoter signup error:', error);
      toast.error(
        error.message || 'Failed to submit application. Please try again.'
      );
      setIsSubmitting(false);
    }
  };

  const renderAddress = (address) => {
    if (!address || typeof address !== 'object') {
      return <InfoValue>Not provided</InfoValue>;
    }

    // Check if address has any data
    if (
      !address.street1 &&
      !address.city &&
      !address.state &&
      !address.zipcode
    ) {
      return <InfoValue>Not provided</InfoValue>;
    }

    return (
      <>
        <InfoItem>
          <InfoLabel>Street Address</InfoLabel>
          <InfoValue>{address.street1 || 'Not provided'}</InfoValue>
        </InfoItem>
        {address.street2 && (
          <InfoItem>
            <InfoLabel>Street Address 2</InfoLabel>
            <InfoValue>{address.street2}</InfoValue>
          </InfoItem>
        )}
        <InfoItem>
          <InfoLabel>City</InfoLabel>
          <InfoValue>{address.city || 'Not provided'}</InfoValue>
        </InfoItem>
        <InfoItem>
          <InfoLabel>State</InfoLabel>
          <InfoValue>{address.state || 'Not provided'}</InfoValue>
        </InfoItem>
        <InfoItem>
          <InfoLabel>ZIP Code</InfoLabel>
          <InfoValue>{address.zipcode || 'Not provided'}</InfoValue>
        </InfoItem>
        {address.country && address.country !== 'US' && (
          <InfoItem>
            <InfoLabel>Country</InfoLabel>
            <InfoValue>{address.country}</InfoValue>
          </InfoItem>
        )}
      </>
    );
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <PageContainer>
        <ContentWrapper>
          <ProgressHeader
            currentStep={getStepNumber('/promoter-signup/review')}
            totalSteps={getTotalSteps()}
            stepTitle="Review Your Application"
          />

          <IntroCard>
            <CardSubtitle>
              Please review your information carefully before submitting your
              application.
            </CardSubtitle>
          </IntroCard>

          {/* User Info Section - Only show if user provided this data */}
          {hasUserInfo && (
            <SectionCard>
              <SectionContainer>
                <SectionHeader>
                  <SectionTitle>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Your Information
                  </SectionTitle>
                  <EditButton onClick={() => handleEdit('user-info')}>
                    Edit
                  </EditButton>
                </SectionHeader>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>First Name</InfoLabel>
                    <InfoValue>{safeDisplay(formData.first_name)}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Last Name</InfoLabel>
                    <InfoValue>{safeDisplay(formData.last_name)}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Email</InfoLabel>
                    <InfoValue>{safeDisplay(formData.email)}</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Zip Code</InfoLabel>
                    <InfoValue>{safeDisplay(formData.zipcode)}</InfoValue>
                  </InfoItem>
                </InfoGrid>
              </SectionContainer>
            </SectionCard>
          )}

          {/* Business Details Section */}
          <SectionCard>
            <SectionContainer>
              <SectionHeader>
                <SectionTitle>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  Business Information
                </SectionTitle>
                <EditButton onClick={() => handleEdit('business-details')}>
                  Edit
                </EditButton>
              </SectionHeader>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Promoter Type</InfoLabel>
                  <InfoValue>
                    {formData.promoter_type === 'track'
                      ? 'Track Promoter'
                      : formData.promoter_type === 'series'
                      ? 'Series Promoter'
                      : formData.promoter_type === 'independent'
                      ? 'Independent Promoter'
                      : 'Not provided'}
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Track/Series Name</InfoLabel>
                  <InfoValue>{safeDisplay(formData.business_dba)}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Legal Business Name</InfoLabel>
                  <InfoValue>{safeDisplay(formData.legal_name)}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Business Email</InfoLabel>
                  <InfoValue>{safeDisplay(formData.business_email)}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Business Phone</InfoLabel>
                  <InfoValue>{safeDisplay(formData.business_phone)}</InfoValue>
                </InfoItem>
              </InfoGrid>
            </SectionContainer>
          </SectionCard>

          {/* Account Owner Section - Only show if not primary owner */}
          {formData.is_primary_owner === false && (
            <SectionCard>
              <SectionContainer>
                <SectionHeader>
                  <SectionTitle>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Account Owner
                  </SectionTitle>
                  <EditButton onClick={() => handleEdit('business-details')}>
                    Edit
                  </EditButton>
                </SectionHeader>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>First Name</InfoLabel>
                    <InfoValue>
                      {safeDisplay(formData.account_owner?.first_name)}
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Last Name</InfoLabel>
                    <InfoValue>
                      {safeDisplay(formData.account_owner?.last_name)}
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Email</InfoLabel>
                    <InfoValue>
                      {safeDisplay(formData.account_owner?.email)}
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Phone</InfoLabel>
                    <InfoValue>
                      {safeDisplay(formData.account_owner?.cellphone)}
                    </InfoValue>
                  </InfoItem>
                </InfoGrid>
              </SectionContainer>
            </SectionCard>
          )}

          {/* Payouts Contact Section - Only show if separate contact */}
          {formData.has_separate_payouts_contact && (
            <SectionCard>
              <SectionContainer>
                <SectionHeader>
                  <SectionTitle>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                    Payouts Contact
                  </SectionTitle>
                  <EditButton onClick={() => handleEdit('business-details')}>
                    Edit
                  </EditButton>
                </SectionHeader>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>First Name</InfoLabel>
                    <InfoValue>
                      {safeDisplay(formData.payouts_contact?.first_name)}
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Last Name</InfoLabel>
                    <InfoValue>
                      {safeDisplay(formData.payouts_contact?.last_name)}
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Email</InfoLabel>
                    <InfoValue>
                      {safeDisplay(formData.payouts_contact?.email)}
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Phone</InfoLabel>
                    <InfoValue>
                      {safeDisplay(formData.payouts_contact?.cellphone)}
                    </InfoValue>
                  </InfoItem>
                </InfoGrid>
              </SectionContainer>
            </SectionCard>
          )}

          {/* Track Address Section - Only show for track promoters */}
          {formData.promoter_type === 'track' && (
            <SectionCard>
              <SectionContainer>
                <SectionHeader>
                  <SectionTitle>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    Track Address
                  </SectionTitle>
                  <EditButton onClick={() => handleEdit('track-address')}>
                    Edit
                  </EditButton>
                </SectionHeader>
                <InfoGrid>{renderAddress(formData.track_address)}</InfoGrid>
              </SectionContainer>
            </SectionCard>
          )}

          {/* Address Sections - Side by Side on Desktop */}
          <SectionsGrid>
            {/* Shipping Address Section */}
            <SectionCard>
              <SectionContainer>
                <SectionHeader>
                  <SectionTitle>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                      />
                    </svg>
                    Shipping Address
                  </SectionTitle>
                  <EditButton onClick={() => handleEdit('shipping-address')}>
                    Edit
                  </EditButton>
                </SectionHeader>
                <InfoGrid>{renderAddress(formData.shipping_address)}</InfoGrid>
              </SectionContainer>
            </SectionCard>

            {/* Mailing Address Section */}
            <SectionCard>
              <SectionContainer>
                <SectionHeader>
                  <SectionTitle>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    Mailing Address
                  </SectionTitle>
                  <EditButton onClick={() => handleEdit('mailing-address')}>
                    Edit
                  </EditButton>
                </SectionHeader>
                <InfoGrid>{renderAddress(formData.mailing_address)}</InfoGrid>
              </SectionContainer>
            </SectionCard>
          </SectionsGrid>

          <ButtonRow>
            <ButtonV2 onClick={handleBack} disabled={isSubmitting}>
              ← Back
            </ButtonV2>
            <ButtonV2
              onClick={handleSubmit}
              disabled={isSubmitting}
              primary={!isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application →'}
            </ButtonV2>
          </ButtonRow>
        </ContentWrapper>
      </PageContainer>
    </ThemeProvider>
  );
};

export default ReviewStep;
